import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthStore } from '../../auth/state';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import { FaSchool } from 'react-icons/fa';
import './discussions.css';

function Discussions() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const iframeRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // # Topic card data
  const discussionTopics = [
    {
      id: 'match',
      title: 'Match Data',
      description: 'Explore match statistics and data to help plan your career path.',
      icon: '📊',
      path: '/blog/discussions/match'
    },
    {
      id: 'life-us',
      title: 'Life in the US',
      description: 'Learn about living in the US as an IMG physician.',
      icon: '🇺🇸',
      path: '/blog/discussions/life-us'
    },
    {
      id: 'specialty',
      title: 'Choosing Specialty',
      description: 'Guidance on selecting the right medical specialty for your career.',
      icon: '🏥',
      path: '/blog/discussions/specialty'
    },
    {
      id: 'medical-school',
      title: 'Medical Schools in the US',
      description: 'List of medical schools in the US.',
      icon: <FaSchool className="blg_ds_topic-icon-svg" />,
      path: '/blog/discussions/medical-school'
    }
  ];

  // # Load user theme preferences
  useEffect(() => {
    const loadThemePreference = async () => {
      try {
        if (user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsDarkMode(userData.themes?.gen_theme === 'dark');
          }
        }
      } catch (error) {
        console.error('Error loading theme preference:', error);
      }
    };

    loadThemePreference();
  }, [user]);

  // # Function to apply theme directly to iframe
  const applyThemeToIframe = (iframe, darkMode) => {
    if (iframe) {
      try {
        // # Update iframe data-theme attribute
        iframe.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        
        // # Update iframe style - remove filters, use proper CSS variables
        iframe.style.colorScheme = darkMode ? 'dark' : 'light';
        iframe.style.backgroundColor = darkMode ? 'var(--blg-ds-bg-primary)' : 'white';
        
        // # Add mobile viewport meta tag
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
        
        // # Attempt to inject theme styles into the iframe
        setTimeout(() => {
          try {
            if (iframe && iframe.contentDocument) {
              iframe.contentDocument.head.appendChild(meta);
              const style = document.createElement('style');
              if (darkMode) {
                // # Dark mode styles without filters
                style.textContent = `
                  /* # Dark mode base styles */
                  body, html {
                    background-color: var(--blg-ds-bg-primary) !important;
                    color-scheme: dark !important;
                    color: var(--blg-ds-text-primary) !important;
                    overflow-x: hidden !important;
                    width: 100% !important;
                    max-width: 100vw !important;
                  }
                  
                  /* # Dark mode text colors */
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: var(--blg-ds-text-primary) !important;
                    word-wrap: break-word !important;
                    overflow-wrap: break-word !important;
                  }
                  
                  /* # Ensure content fits mobile screens */
                  img, video, iframe {
                    max-width: 100% !important;
                    height: auto !important;
                  }
                  
                  /* # Dark mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: var(--blg-ds-bg-primary) !important;
                    max-width: 100vw !important;
                    overflow-x: hidden !important;
                  }
                `;
              } else {
                // # Light mode styles with mobile optimizations
                style.textContent = `
                  body, html {
                    background-color: white !important;
                    color-scheme: light !important;
                    color: #2c3e50 !important;
                    overflow-x: hidden !important;
                    width: 100% !important;
                    max-width: 100vw !important;
                  }
                  
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: #2c3e50 !important;
                    word-wrap: break-word !important;
                    overflow-wrap: break-word !important;
                  }
                  
                  /* # Ensure content fits mobile screens */
                  img, video, iframe {
                    max-width: 100% !important;
                    height: auto !important;
                  }
                  
                  /* # Light mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: white !important;
                    max-width: 100vw !important;
                    overflow-x: hidden !important;
                  }
                `;
              }
              iframe.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle iframe load events
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        // # Apply theme directly to iframe
        applyThemeToIframe(iframeRef.current, isDarkMode);
        
        // # Try to access iframe content to inject error suppression and styling
        const style = document.createElement('style');
        style.textContent = `
          /* # Hide elements that might expose URLs */
          .notion-overlay-container, 
          .notion-login-prompt,
          .notion-help-button,
          .notion-cursor-listener,
          .notion-topbar,
          .notion-selectable[data-block-id],
          .notion-frame > div[style*="fixed"],
          .notion-frame > div[style*="absolute"],
          .notion-link-token,
          .notion-collection-card__content,
          .notion-header__icon-wrapper {
            display: none !important;
          }
          
          /* # Force theme colors */
          body, html {
            background-color: ${isDarkMode ? 'var(--blg-ds-bg-primary)' : 'white'} !important;
            color-scheme: ${isDarkMode ? 'dark' : 'light'} !important;
            color: ${isDarkMode ? 'var(--blg-ds-text-primary)' : '#2c3e50'} !important;
          }
        `;
        
        // # Attempt to inject the style into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.head.appendChild(style);
              
              // # Also try to intercept console methods in the iframe
              const script = document.createElement('script');
              script.textContent = `
                // # Block network requests that might leak URLs
                const originalFetch = window.fetch;
                window.fetch = function(url, options) {
                  // # Allow same origin requests
                  return originalFetch.apply(this, arguments);
                };
              `;
              iframeRef.current.contentDocument.head.appendChild(script);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Apply theme directly to iframe if exists
      if (iframeRef.current) {
        applyThemeToIframe(iframeRef.current, newTheme);
      }
      
      if (user?.uid) {
        await setDoc(doc(db, 'users', user.uid), {
          themes: { gen_theme: newTheme ? 'dark' : 'light' }
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  // # Handle card hover
  const handleCardHover = (topicId) => {
    setHoveredCard(topicId);
  };

  return (
    <div className={`blg_ds_discussions-container ${isDarkMode ? 'blg_ds_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="blg_ds_discussions-content">
        <div className="blg_ds_section-header">
          <h1>Discussion Topics</h1>
          <p>Select a topic to join the discussion</p>
        </div>
        
        <div className="blg_ds_topics-grid">
          {discussionTopics.map((topic) => (
            <div 
              key={topic.id} 
              className={`blg_ds_topic-card ${hoveredCard === topic.id ? 'blg_ds_card-hovered' : ''}`}
              onClick={() => navigate(topic.path)}
              onMouseEnter={() => handleCardHover(topic.id)}
              onMouseLeave={() => handleCardHover(null)}
            >
              <div className="blg_ds_topic-icon">{topic.icon}</div>
              <h2 className="blg_ds_topic-title">{topic.title}</h2>
              <p className="blg_ds_topic-description">{topic.description}</p>
              <div className="blg_ds_view-button">
                <span>View Discussion</span>
                <svg 
                  className="blg_ds_arrow-icon" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    d="M5 12h14M12 5l7 7-7 7" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
}

export default Discussions; 